"use client";

// The ONE place routing decisions happen. Derives FlowState from the store,
// triggers the data loads each state needs, and issues at most one redirect to
// the route that matches the current flow. No feature code calls router.replace
// for flow control — this replaces the Flutter go_router redirect race.

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  type FlowState,
  deriveFlowState,
  useApp,
} from "@/store/appStore";

const FLOW_ROUTE: Record<FlowState, string> = {
  checkingAuth: "/",
  loggedOut: "/login",
  authing: "/login",
  checkingElections: "/",
  needsPrevalidation: "/prevalidation",
  readyToVote: "/start-voting",
  voting: "/candidates",
  confirming: "/confirmation",
  voted: "/success",
  voteEnded: "/vote-ended",
};

export default function FlowGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const state = useApp();
  const flow = deriveFlowState(state);

  // ---- data-load side effects ----
  useEffect(() => {
    if (state.auth.status !== "authenticated") return;
    if (state.available.status === "idle") {
      void state.loadAvailableElections();
    }
  }, [state.auth.status, state.available.status, state]);

  useEffect(() => {
    if (state.auth.status !== "authenticated") return;
    if (state.active.status !== "idle") return;
    // Only auto-load once an election is explicitly chosen (URL param or the
    // /start-voting picker). Without this, multi-election users would skip the
    // picker and land on list[0]. readyToVote -> /start-voting handles the rest.
    if (state.electionId) void state.loadActiveElection();
  }, [state.auth.status, state.active.status, state.electionId, state]);

  // ---- single redirect ----
  useEffect(() => {
    // Holding states (restoring session / loading the chosen election) resolve
    // in a moment. Don't yank the voter to the splash while they resolve — that
    // would flash-bounce a refresh on /candidates or /success. Preserve the URL
    // and let the real flow state redirect once it's known (mirrors the Flutter
    // "preserve URL during auth loading" behavior).
    if (flow === "checkingAuth" || flow === "checkingElections") return;

    const target = FLOW_ROUTE[flow];
    const here = pathname?.replace(/\/$/, "") || "/";
    const dest = target.replace(/\/$/, "") || "/";
    if (here !== dest) router.replace(target);
  }, [flow, pathname, router]);

  return <>{children}</>;
}
